import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/functions/_generated/api';
import { adminSession, type FeatureFlag } from '@/lib/api';
import { friendlyError } from '@/lib/errors';
import { PlainConfirmModal } from '@/components/Modals';

/**
 * /dashboard/flags — admin-toggleable feature gates.
 *
 * Renders the in-code registry of flags (defined in
 * src/convex/functions/admin/flags.ts) with current state, scope
 * chip, description, and a per-flag on/off toggle.
 *
 * Reactive: the query re-subscribes whenever a `settings` row
 * changes, so flipping the toggle pushes a fresh value back to
 * every other open admin tab too. No optimistic UI needed — the
 * mutation round-trip + reactive push is fast enough.
 */
export function FlagsPage() {
  const token = adminSession.getToken() ?? '';
  const data = useQuery(
    api.admin.flags.listFlags,
    token ? { token } : 'skip',
  );
  const setFlagMut = useMutation(api.admin.flags.setFlag);
  const flags = data?.flags ?? null;
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const toggle = async (key: string) => {
    if (!flags) return;
    const current = flags.find((f: FeatureFlag) => f.key === key);
    if (!current) return;
    const next = !current.enabled;
    setSavingKey(key);
    setErr(null);
    try {
      await setFlagMut({ token, key, enabled: next });
    } catch (e) {
      setErr(friendlyError(e, 'setFlag'));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div>
      <header style={{ marginBottom: 24 }}>
        <h2 className="serif" style={{ fontSize: 30, fontWeight: 500, fontStyle: 'italic', margin: 0 }}>
          Feature flags
        </h2>
        <p style={{ color: 'var(--ink-3)', fontSize: 13, marginTop: 6, maxWidth: 640 }}>
          Toggle admin-controlled features for the public app.
          {' '}
          <strong style={{ color: 'var(--ink-2)' }}>User-scoped</strong> flags
          {' '}gate features per signed-in user.
          {' '}
          <strong style={{ color: 'var(--ink-2)' }}>System</strong> flags apply
          to everyone (the signups gate is the canonical example — there's
          no user identity at signup time).
        </p>
      </header>

      {err && (
        <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '10px 14px', borderRadius: 12, fontSize: 13, marginBottom: 16 }}>
          {err}
        </div>
      )}

      {!flags ? (
        <div style={{ color: 'var(--ink-3)', padding: 32, textAlign: 'center' }}>Loading…</div>
      ) : flags.length === 0 ? (
        <div style={{ color: 'var(--ink-3)', padding: 32, textAlign: 'center' }}>
          No feature flags registered.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {flags.map((f: FeatureFlag) => (
            <FlagRow
              key={f.key}
              flag={f}
              busy={savingKey === f.key}
              onToggle={() => void toggle(f.key)}
            />
          ))}
        </div>
      )}

      <DiscoverySection />
      <ConsentSection />
    </div>
  );
}

// ─── Legal consent reset ───────────────────────────────────────────
//
// "Clear consent" bumps the global reset timestamp(s) (settings keys
// consent.privacy_reset_at / consent.terms_reset_at; backend in
// admin/consent.ts). Every user whose acceptance predates the reset is
// forced to re-accept via the non-dismissable in-app consent gate on
// their next load. Use after publishing an updated Privacy Policy or
// Terms.

function fmtReset(ts: number | null): string {
  if (!ts) return 'never cleared';
  try {
    return `last cleared ${new Date(ts).toLocaleString()}`;
  } catch {
    return 'last cleared';
  }
}

function ConsentSection() {
  const token = adminSession.getToken() ?? '';
  const config = useQuery(
    api.admin.consent.getConsentConfig,
    token ? { token } : 'skip',
  );
  const clearMut = useMutation(api.admin.consent.clearConsent);

  const [busy, setBusy] = useState<'privacy' | 'terms' | 'both' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Which target the confirm modal is armed for (null = closed).
  const [confirmTarget, setConfirmTarget] = useState<'privacy' | 'terms' | 'both' | null>(null);

  const targetLabel = (t: 'privacy' | 'terms' | 'both') =>
    t === 'both' ? 'both the Privacy Policy and Terms' :
    t === 'privacy' ? 'the Privacy Policy' : 'the Terms';

  const runClear = async (target: 'privacy' | 'terms' | 'both') => {
    setBusy(target);
    setErr(null);
    try {
      await clearMut({ token, target });
    } catch (e) {
      setErr(friendlyError(e, 'clearConsent'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid var(--line)' }}>
      <h3 className="serif" style={{ fontSize: 22, fontWeight: 500, fontStyle: 'italic', margin: 0 }}>
        Legal consent
      </h3>
      <p style={{ color: 'var(--ink-3)', fontSize: 13, marginTop: 6, maxWidth: 640 }}>
        After updating the Privacy Policy or Terms, clear consent to force
        every user to review and re-accept before continuing to use the app.
      </p>

      {err && (
        <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '10px 14px', borderRadius: 12, fontSize: 13, margin: '14px 0' }}>
          {err}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
        <ConsentRow
          label="Privacy Policy"
          sub={config ? fmtReset(config.privacyResetAt) : 'Loading…'}
          busy={busy === 'privacy'}
          disabled={busy !== null}
          onClear={() => setConfirmTarget('privacy')}
        />
        <ConsentRow
          label="Terms & Conditions"
          sub={config ? fmtReset(config.termsResetAt) : 'Loading…'}
          busy={busy === 'terms'}
          disabled={busy !== null}
          onClear={() => setConfirmTarget('terms')}
        />
        <div style={{ marginTop: 4 }}>
          <button
            type="button"
            onClick={() => setConfirmTarget('both')}
            disabled={busy !== null}
            style={{
              padding: '10px 16px',
              borderRadius: 10,
              border: '1px solid #a8331f',
              background: 'transparent',
              color: '#a8331f',
              fontSize: 13,
              fontWeight: 600,
              cursor: busy !== null ? 'not-allowed' : 'pointer',
              opacity: busy !== null ? 0.6 : 1,
            }}
          >
            {busy === 'both' ? 'Clearing…' : 'Clear both'}
          </button>
        </div>
      </div>

      <PlainConfirmModal
        open={confirmTarget !== null}
        title="Clear consent?"
        body={
          confirmTarget
            ? `You're about to clear consent for ${targetLabel(confirmTarget)}. Every user will be forced to re-accept on their next app load. This cannot be undone.`
            : ''
        }
        confirmLabel="Clear consent"
        confirmStyle="danger"
        onCancel={() => setConfirmTarget(null)}
        onConfirm={() => {
          const t = confirmTarget;
          setConfirmTarget(null);
          if (t) void runClear(t);
        }}
      />
    </section>
  );
}

function ConsentRow({
  label, sub, busy, disabled, onClear,
}: {
  label: string;
  sub: string;
  busy: boolean;
  disabled: boolean;
  onClear: () => void;
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 16, padding: '14px 16px', background: 'var(--surface)',
      border: '1px solid var(--line)', borderRadius: 12,
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{sub}</div>
      </div>
      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        style={{
          padding: '8px 14px', borderRadius: 10, border: '1px solid var(--line-strong)',
          background: 'transparent', color: 'var(--ink)', fontSize: 13, fontWeight: 600,
          cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
          whiteSpace: 'nowrap',
        }}
      >
        {busy ? 'Clearing…' : 'Clear consent'}
      </button>
    </div>
  );
}

// ─── Discovery cooldown ────────────────────────────────────────────
//
// Runtime control for the Discover re-surface cooldown (settings key
// `discovery.cooldown_minutes`; backend in admin/flags.ts). Stored as
// minutes; the editor offers a value + unit (min/hours/days) for
// convenience. Per-deployment — dev and prod hold independent rows.

type Unit = 'min' | 'hours' | 'days';
const UNIT_MINUTES: Record<Unit, number> = { min: 1, hours: 60, days: 1440 };

/** Pick the largest unit that represents `minutes` without a fraction. */
function pickUnit(minutes: number): Unit {
  if (minutes !== 0 && minutes % UNIT_MINUTES.days === 0) return 'days';
  if (minutes !== 0 && minutes % UNIT_MINUTES.hours === 0) return 'hours';
  return 'min';
}

/** Human-readable summary of a minutes value for the "current" line. */
function humanizeMinutes(minutes: number): string {
  if (minutes === 0) return '0 min · re-surfaces immediately';
  const u = pickUnit(minutes);
  const n = minutes / UNIT_MINUTES[u];
  const label = u === 'days' ? 'day' : u === 'hours' ? 'hour' : 'min';
  const plural = u === 'min' ? '' : n === 1 ? '' : 's';
  return `${n} ${label}${plural}`;
}

function DiscoverySection() {
  const token = adminSession.getToken() ?? '';
  const config = useQuery(
    api.admin.flags.discoveryConfig,
    token ? { token } : 'skip',
  );
  const setCooldownMut = useMutation(api.admin.flags.setDiscoveryCooldown);

  const [value, setValue] = useState('');
  const [unit, setUnit] = useState<Unit>('min');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Seed the editor from the server value, and re-seed after a save
  // (touched resets) so the field reflects the clamped/stored value.
  useEffect(() => {
    if (!config || touched) return;
    const u = pickUnit(config.cooldownMinutes);
    setUnit(u);
    setValue(String(config.cooldownMinutes / UNIT_MINUTES[u]));
  }, [config, touched]);

  const save = async () => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      setErr('Enter a non-negative number.');
      return;
    }
    const minutes = Math.round(num * UNIT_MINUTES[unit]);
    setSaving(true);
    setErr(null);
    try {
      await setCooldownMut({ token, cooldownMinutes: minutes });
      setTouched(false); // re-sync to the stored (clamped) value
    } catch (e) {
      setErr(friendlyError(e, 'setDiscoveryCooldown'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section style={{ marginTop: 36 }}>
      <h3 className="serif" style={{ fontSize: 22, fontWeight: 500, fontStyle: 'italic', margin: '0 0 6px' }}>
        Discovery
      </h3>
      <p style={{ color: 'var(--ink-3)', fontSize: 13, marginTop: 0, marginBottom: 16, maxWidth: 640 }}>
        How long a rated card stays hidden before it re-surfaces in the
        Discover deck.
        {' '}
        <strong style={{ color: 'var(--ink-2)' }}>Per-deployment</strong>
        {' '}— set independently on dev and prod.
      </p>

      {err && (
        <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '10px 14px', borderRadius: 12, fontSize: 13, marginBottom: 16 }}>
          {err}
        </div>
      )}

      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: 14,
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--ink)' }}>Re-surface cooldown</span>
            <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1 }}>
              discovery.cooldown_minutes
            </span>
          </div>
          <div style={{ color: 'var(--ink-3)', fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
            {config
              ? (
                <>
                  Current: <strong style={{ color: 'var(--ink-2)' }}>{humanizeMinutes(config.cooldownMinutes)}</strong>
                  {config.isDefault && (
                    <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, marginLeft: 8 }}>
                      (default)
                    </span>
                  )}
                </>
              )
              : 'Loading…'}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto' }}>
          <input
            type="number"
            min={0}
            step={1}
            value={value}
            disabled={!config || saving}
            onChange={(e) => { setTouched(true); setValue(e.target.value); }}
            style={{
              width: 88,
              padding: '8px 10px',
              borderRadius: 10,
              border: '1px solid var(--line-strong)',
              background: 'var(--bg)',
              color: 'var(--ink)',
              fontSize: 14,
            }}
          />
          <select
            value={unit}
            disabled={!config || saving}
            onChange={(e) => { setTouched(true); setUnit(e.target.value as Unit); }}
            style={{
              padding: '8px 10px',
              borderRadius: 10,
              border: '1px solid var(--line-strong)',
              background: 'var(--bg)',
              color: 'var(--ink)',
              fontSize: 14,
            }}
          >
            <option value="min">min</option>
            <option value="hours">hours</option>
            <option value="days">days</option>
          </select>
          <button
            type="button"
            onClick={() => void save()}
            disabled={!config || saving}
            style={{
              padding: '8px 16px',
              borderRadius: 10,
              border: 'none',
              background: 'var(--coral)',
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <RankingWeightsCard config={config} token={token} />

      {config && config.environment !== 'prod' && (
        <MapCheckInRefreshCard token={token} />
      )}
    </section>
  );
}

// ─── Map check-in refresh (dev-only) ───────────────────────────────
//
// The Discover live map only shows users with an ACTIVE check-in, and
// check-ins lapse within the hour. Seed profiles never check themselves in,
// so without this the dev map is permanently empty. This mints a synthetic
// check-in for every usable test profile and re-seeds the geoLive index.
// Dev-only: hidden on prod via discoveryConfig.environment, and the backend
// rejects on prod regardless.

function MapCheckInRefreshCard({ token }: { token: string }) {
  const refreshMut = useMutation(api.admin.flags.refreshMapCheckIns);
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const refresh = async () => {
    setBusy(true);
    setErr(null);
    try {
      const res = await refreshMut({ token });
      setCount(res.refreshed);
    } catch (e) {
      setErr(friendlyError(e, 'refreshMapCheckIns'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 14,
        padding: '14px 18px',
        marginTop: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: '1 1 320px', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--ink)' }}>Refresh map check-ins</span>
          <span
            className="mono"
            style={{
              background: 'rgba(232,154,110,0.16)',
              color: 'var(--coral-ink)',
              fontSize: 10,
              letterSpacing: 1,
              padding: '2px 8px',
              borderRadius: 100,
              textTransform: 'uppercase',
              fontWeight: 600,
            }}
          >
            Dev only
          </span>
        </div>
        <div style={{ color: 'var(--ink-3)', fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
          Checks every published test profile in for an hour so they appear on
          the Discover live map. Each stays pinned at its own coords — this
          doesn't move anyone closer to you.
          {count !== null && (
            <>
              {' '}
              <strong style={{ color: 'var(--ink-2)' }}>
                Refreshed {count} {count === 1 ? 'profile' : 'profiles'}.
              </strong>
            </>
          )}
        </div>
        {err && (
          <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '10px 14px', borderRadius: 12, fontSize: 13, marginTop: 12 }}>
            {err}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => void refresh()}
        disabled={busy}
        style={{
          padding: '8px 16px',
          borderRadius: 10,
          border: 'none',
          background: 'var(--coral)',
          color: '#fff',
          fontSize: 14,
          fontWeight: 600,
          cursor: busy ? 'wait' : 'pointer',
          opacity: busy ? 0.6 : 1,
          flex: '0 0 auto',
        }}
      >
        {busy ? 'Refreshing…' : 'Refresh now'}
      </button>
    </div>
  );
}

// ─── Discovery ranking weights ─────────────────────────────────────
//
// Per-signal weights for the Discover re-rank (settings key
// `discovery.ranking_weights`; backend in admin/flags.ts, scoring in
// discovery.ts). Each signal is normalized to 0..1 and the card score is
// Σ weightᵢ · signalᵢ; a weight of 0 disables that signal. Clamped 0–5.

type WeightKey = 'distance' | 'fairness' | 'likedYou' | 'recency' | 'completeness';

const WEIGHT_FIELDS: { key: WeightKey; label: string }[] = [
  { key: 'distance', label: 'Distance' },
  { key: 'fairness', label: 'Fairness' },
  { key: 'likedYou', label: 'Liked you' },
  { key: 'recency', label: 'Recency' },
  { key: 'completeness', label: 'Completeness' },
];

type DiscoveryConfig = {
  weights: Record<WeightKey, number>;
  weightsAreDefault: boolean;
  defaultWeights: Record<WeightKey, number>;
};

function RankingWeightsCard({
  config,
  token,
}: {
  config: DiscoveryConfig | undefined;
  token: string;
}) {
  const setWeightsMut = useMutation(api.admin.flags.setRankingWeights);

  const [values, setValues] = useState<Record<WeightKey, string>>(
    () => emptyWeightStrings(),
  );
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Seed the editor from the server value; re-seed after a save (touched
  // resets) so each field reflects the clamped/stored weight.
  useEffect(() => {
    if (!config || touched) return;
    const next = emptyWeightStrings();
    for (const { key } of WEIGHT_FIELDS) next[key] = String(config.weights[key]);
    setValues(next);
  }, [config, touched]);

  const persist = async (weights: Record<WeightKey, number>) => {
    setSaving(true);
    setErr(null);
    try {
      await setWeightsMut({ token, weights });
      setTouched(false); // re-sync to the stored (clamped) values
    } catch (e) {
      setErr(friendlyError(e, 'setRankingWeights'));
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    const weights = emptyWeightNumbers();
    for (const { key } of WEIGHT_FIELDS) {
      const num = Number(values[key]);
      if (!Number.isFinite(num) || num < 0) {
        setErr('Enter a non-negative number for every weight.');
        return;
      }
      weights[key] = num;
    }
    await persist(weights);
  };

  const resetDefaults = async () => {
    if (!config) return;
    setTouched(true);
    await persist({ ...config.defaultWeights });
  };

  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 14,
        padding: '14px 18px',
        marginTop: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--ink)' }}>Ranking weights</span>
        <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1 }}>
          discovery.ranking_weights
        </span>
        {config?.weightsAreDefault && (
          <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1 }}>
            (default)
          </span>
        )}
      </div>
      <div style={{ color: 'var(--ink-3)', fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
        Per-signal weights for the Discover re-rank — score is Σ weight · signal.
        Set a weight to 0 to disable that signal. Range 0–5.
        {' '}
        <strong style={{ color: 'var(--ink-2)' }}>Per-deployment</strong>
        {' '}— dev and prod are independent.
      </div>

      {err && (
        <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '10px 14px', borderRadius: 12, fontSize: 13, marginTop: 12 }}>
          {err}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
        {WEIGHT_FIELDS.map(({ key, label }) => (
          <div
            key={key}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
              <span style={{ fontSize: 14, color: 'var(--ink)' }}>{label}</span>
              <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1 }}>
                {key}
              </span>
              {config && (
                <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                  current: <strong style={{ color: 'var(--ink-2)' }}>{config.weights[key]}</strong>
                  {config.weightsAreDefault && (
                    <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, marginLeft: 6 }}>
                      (default)
                    </span>
                  )}
                </span>
              )}
            </div>
            <input
              type="number"
              min={0}
              max={5}
              step={0.1}
              value={values[key]}
              disabled={!config || saving}
              onChange={(e) => {
                setTouched(true);
                setValues((prev) => ({ ...prev, [key]: e.target.value }));
              }}
              style={{
                width: 88,
                padding: '8px 10px',
                borderRadius: 10,
                border: '1px solid var(--line-strong)',
                background: 'var(--bg)',
                color: 'var(--ink)',
                fontSize: 14,
                flex: '0 0 auto',
              }}
            />
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => void resetDefaults()}
          disabled={!config || saving || config.weightsAreDefault}
          style={{
            padding: '8px 16px',
            borderRadius: 10,
            border: '1px solid var(--line-strong)',
            background: 'transparent',
            color: 'var(--ink-2)',
            fontSize: 14,
            fontWeight: 600,
            cursor: !config || saving || config.weightsAreDefault ? 'default' : 'pointer',
            opacity: !config || saving || config.weightsAreDefault ? 0.5 : 1,
          }}
        >
          Reset to defaults
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={!config || saving}
          style={{
            padding: '8px 16px',
            borderRadius: 10,
            border: 'none',
            background: 'var(--coral)',
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            cursor: saving ? 'wait' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? 'Saving…' : 'Save weights'}
        </button>
      </div>
    </div>
  );
}

function emptyWeightStrings(): Record<WeightKey, string> {
  return { distance: '', fairness: '', likedYou: '', recency: '', completeness: '' };
}

function emptyWeightNumbers(): Record<WeightKey, number> {
  return { distance: 0, fairness: 0, likedYou: 0, recency: 0, completeness: 0 };
}

function FlagRow({ flag, busy, onToggle }: { flag: FeatureFlag; busy: boolean; onToggle: () => void }) {
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 14,
        padding: '14px 18px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
      }}
    >
      {/* Small basis so the switch stays beside the text on a phone. */}
      <div style={{ flex: '1 1 200px', minWidth: 0, overflowWrap: 'anywhere' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--ink)' }}>{flag.label}</span>
          <ScopeChip scope={flag.scope} />
          <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1 }}>
            {flag.key}
          </span>
        </div>
        <div style={{ color: 'var(--ink-3)', fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
          {flag.description}
        </div>
        {flag.enabled !== flag.default && (
          <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, marginTop: 6 }}>
            Default: {flag.default ? 'ON' : 'OFF'} · Currently overridden
          </div>
        )}
      </div>
      <Switch enabled={flag.enabled} busy={busy} onToggle={onToggle} />
    </div>
  );
}

function Switch({
  enabled,
  busy,
  onToggle,
}: { enabled: boolean; busy: boolean; onToggle: () => void }) {
  const W = 52;
  const H = 30;
  const THUMB = 24;
  const PAD = (H - THUMB) / 2;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={onToggle}
      disabled={busy}
      style={{
        position: 'relative',
        width: W,
        height: H,
        borderRadius: H / 2,
        border: 'none',
        padding: 0,
        background: enabled ? 'var(--coral)' : 'var(--line-strong)',
        cursor: busy ? 'wait' : 'pointer',
        opacity: busy ? 0.6 : 1,
        transition: 'background 160ms ease',
        flex: '0 0 auto',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: PAD,
          left: enabled ? W - THUMB - PAD : PAD,
          width: THUMB,
          height: THUMB,
          borderRadius: THUMB / 2,
          background: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.22)',
          transition: 'left 160ms ease',
          display: 'block',
        }}
      />
    </button>
  );
}

function ScopeChip({ scope }: { scope: 'system' | 'user' }) {
  const isSystem = scope === 'system';
  return (
    <span
      className="mono"
      style={{
        background: isSystem ? 'rgba(43,29,20,0.08)' : 'rgba(232,154,110,0.16)',
        color: isSystem ? 'var(--ink-2)' : 'var(--coral-ink)',
        fontSize: 10,
        letterSpacing: 1,
        padding: '2px 8px',
        borderRadius: 100,
        textTransform: 'uppercase',
        fontWeight: 600,
      }}
      title={isSystem
        ? 'Applies to everyone.'
        : 'Resolved per signed-in user.'}
    >
      {isSystem ? 'System' : 'User'}
    </span>
  );
}
